import { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import {
  createRoomShellGridTextureCanvas,
  createWalkableShapeGeometry,
  getRoomShellWallTint,
  getRoomShellWallDescriptors,
  ROOM_SHELL_DEBUG_FLOOR_COLOR,
  ROOM_SHELL_DEBUG_FLOOR_OPACITY,
  ROOM_SHELL_DEBUG_GRID_STROKE,
  ROOM_SHELL_DEBUG_WALL_FILL_OPACITY,
  ROOM_SHELL_DEBUG_WALL_GRID_OPACITY,
} from '../../../lib/world/room-shell-geometry';

export default function SceneDebugOverlay({ walkableShape = 'square' }) {
  const floorGeometry = useMemo(() => createWalkableShapeGeometry(walkableShape), [walkableShape]);

  const walls = useMemo(
    () =>
      getRoomShellWallDescriptors(walkableShape).map((wall) => {
        const texture = new THREE.CanvasTexture(
          createRoomShellGridTextureCanvas(ROOM_SHELL_DEBUG_GRID_STROKE)
        );
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(wall.repeatX, wall.repeatY);
        texture.colorSpace = THREE.SRGBColorSpace;

        return {
          ...wall,
          texture,
        };
      }),
    [walkableShape]
  );

  useEffect(
    () => () => {
      floorGeometry.dispose();
      for (const wall of walls) {
        wall.texture.dispose();
      }
    },
    [floorGeometry, walls]
  );

  return (
    <group renderOrder={30}>
      <mesh
        geometry={floorGeometry}
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, 0.015, 0]}
        renderOrder={30}
      >
        <meshBasicMaterial
          color={ROOM_SHELL_DEBUG_FLOOR_COLOR}
          transparent
          opacity={ROOM_SHELL_DEBUG_FLOOR_OPACITY}
          side={THREE.DoubleSide}
          depthWrite={false}
          depthTest={false}
        />
      </mesh>

      {walls.map((wall) => {
        const wallTint = getRoomShellWallTint(wall.key);

        return (
          <group key={wall.key}>
            <mesh
              position={[wall.position.x, wall.position.y, wall.position.z]}
              rotation={[wall.rotation.x, wall.rotation.y, wall.rotation.z]}
              renderOrder={31}
            >
              <planeGeometry args={[wall.width, wall.height]} />
              <meshBasicMaterial
                color={wallTint}
                transparent
                opacity={ROOM_SHELL_DEBUG_WALL_FILL_OPACITY}
                side={THREE.DoubleSide}
                depthWrite={false}
                depthTest={false}
              />
            </mesh>

            <mesh
              position={[wall.position.x, wall.position.y, wall.position.z]}
              rotation={[wall.rotation.x, wall.rotation.y, wall.rotation.z]}
              renderOrder={32}
            >
              <planeGeometry args={[wall.width, wall.height]} />
              <meshBasicMaterial
                map={wall.texture}
                color={wallTint}
                transparent
                opacity={ROOM_SHELL_DEBUG_WALL_GRID_OPACITY}
                side={THREE.DoubleSide}
                depthWrite={false}
                depthTest={false}
              />
            </mesh>
          </group>
        );
      })}
    </group>
  );
}
